import { Button } from "components/DesignSystem/Button/Button";
import ChatCard from "components/DesignSystem/ChatCard/ChatCard";
import { FormikForm } from "components/FormikForm/FormikForm";
import {
  CUSTOM_FIELD_TYPE_CHECKBOX,
  CUSTOM_FIELD_TYPE_COUNTRY_SELECTOR,
  CUSTOM_FIELD_TYPE_ENTITY_SELECTOR,
} from "constants/aiQuestionCardFields";
import { AI_QUESTION_CARD_CUSTOM_TYPE } from "constants/chatType";
import { REQUESTED, SUBMITTED } from "constants/sensitiveData";
import { useToast } from "hooks/useToast";
import { useMemo, useRef } from "react";
import { useChannelStateContext } from "stream-chat-react";
import { useSubmitAIQuestionCardMutation } from "store/apis/aiQuestionCard";
import * as Yup from "yup";
import { renderCustomField } from "./CustomFieldDispatcher";
import {
  CustomField,
  CustomFieldFormValues,
  fieldNameForIndex,
} from "./customField.types";
import { BackendError } from "types/utils/error";

type AIQuestionCardProps = {
  customData: {
    header?: string;
    metadata: { fields: CustomField[] };
  };
  messageId: string;
  entityId?: string;
  groupId: string;
  cardStatus: string;
};

const EMPTY_FIELDS: CustomField[] = [];

const isMultiValueField = (field: CustomField): boolean =>
  field.type === CUSTOM_FIELD_TYPE_CHECKBOX || Boolean(field.is_multi_select);

const buildInitialValues = (fields: CustomField[]): CustomFieldFormValues => {
  const initialValues: CustomFieldFormValues = {};
  fields.forEach((field, index) => {
    const name = fieldNameForIndex(index);
    initialValues[name] = isMultiValueField(field)
      ? (field.value as string[]) || []
      : (field.value as string) || "";
  });
  return initialValues;
};

const buildValidationSchema = (fields: CustomField[]) => {
  const shape: Record<string, Yup.AnySchema> = {};
  fields.forEach((field, index) => {
    if (!field.is_required) return;

    const name = fieldNameForIndex(index);
    shape[name] = isMultiValueField(field)
      ? Yup.array().min(1, `${field.label} is required`)
      : Yup.string().trim().required(`${field.label} is required`);
  });
  return Yup.object().shape(shape);
};

const findFieldNameByType = (
  fields: CustomField[],
  type: CustomField["type"]
) => {
  const index = fields.findIndex((field) => field.type === type);
  return index === -1 ? undefined : fieldNameForIndex(index);
};

const labelForValue = (field: CustomField, value: string): string => {
  const option = field.options?.find((item) => item.value === value);
  return option?.label ?? value;
};

const buildSummaryMessage = (
  fields: CustomField[],
  values: CustomFieldFormValues,
  resolvedLabels: Record<string, string>
) =>
  fields
    .map((field, index) => {
      const name = fieldNameForIndex(index);
      const rawValue = values[name];
      const displayValue = Array.isArray(rawValue)
        ? rawValue.map((value) => labelForValue(field, value)).join(", ")
        : labelForValue(field, rawValue);
      return `${field.label}:\n ${resolvedLabels[name] || displayValue}`;
    })
    .join("\n");

const buildAnsweredFields = (
  fields: CustomField[],
  values: CustomFieldFormValues
): CustomField[] =>
  fields.map((field, index) => ({
    ...field,
    value: values[fieldNameForIndex(index)],
  }));

export const AIQuestionCard = ({
  customData,
  messageId,
  entityId: entityIdFromProps,
  groupId,
  cardStatus,
}: AIQuestionCardProps) => {
  const fields = customData.metadata?.fields || EMPTY_FIELDS;
  const { channel } = useChannelStateContext();
  const { alertToast } = useToast();
  const [submitAIQuestionCard] = useSubmitAIQuestionCardMutation();
  const resolvedLabelsRef = useRef<Record<string, string>>({});
  const isSubmitted = cardStatus === SUBMITTED;
  const showSubmitButton = cardStatus === REQUESTED;

  const entityFieldName = useMemo(
    () => findFieldNameByType(fields, CUSTOM_FIELD_TYPE_ENTITY_SELECTOR),
    [fields]
  );
  const countryFieldName = useMemo(
    () => findFieldNameByType(fields, CUSTOM_FIELD_TYPE_COUNTRY_SELECTOR),
    [fields]
  );
  const validationSchema = useMemo(
    () => buildValidationSchema(fields),
    [fields]
  );
  const initialValues = useMemo(() => buildInitialValues(fields), [fields]);

  const handleLabelResolved = (name: string, label: string) => {
    resolvedLabelsRef.current[name] = label;
  };

  const handleSubmit = async (values: CustomFieldFormValues) => {
    const answeredFields = buildAnsweredFields(fields, values);

    try {
      await submitAIQuestionCard({
        groupId,
        messageId,
        customType: AI_QUESTION_CARD_CUSTOM_TYPE,
        metadata: { fields: answeredFields },
      }).unwrap();
      await channel.sendMessage({
        text: buildSummaryMessage(fields, values, resolvedLabelsRef.current),
        custom_data: {
          metadata: { fields: answeredFields },
        },
      });
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  if (fields.length === 0) {
    return null;
  }

  return (
    <ChatCard.Root size="block">
      {customData.header && (
        <ChatCard.Header>{customData.header}</ChatCard.Header>
      )}
      <ChatCard.Body>
        <FormikForm<CustomFieldFormValues>
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <div className="t-flex t-flex-col t-gap-4">
              {fields.map((field, index) => (
                <div key={fieldNameForIndex(index)}>
                  {renderCustomField({
                    field,
                    name: fieldNameForIndex(index),
                    groupId,
                    entityIdFromProps,
                    entityFieldName,
                    countryFieldName,
                    disabled: isSubmitted,
                    onLabelResolved: handleLabelResolved,
                  })}
                </div>
              ))}
              {showSubmitButton && (
                <Button
                  customType="primary"
                  size="small"
                  type="submit"
                  isLoading={isSubmitting}
                >
                  Submit
                </Button>
              )}
            </div>
          )}
        </FormikForm>
      </ChatCard.Body>
    </ChatCard.Root>
  );
};
